import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSession = () => {
  let s = localStorage.getItem("mx_chat_session");
  if (!s) { s = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("mx_chat_session", s); }
  return s;
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hi — I'm the Molecularix research concierge. Ask me about any compound, specs, or availability." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const session = useRef(getSession());

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/growth/chat`, { session_id: session.current, message: text });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry — I'm unavailable right now. Please reach us on WhatsApp for help." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((v) => !v)} data-testid="chat-toggle"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-xl hover:bg-[#C5A028] transition-colors"
        aria-label="Open research assistant">
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-[#0C0C0C] border border-zinc-800 flex flex-col shadow-2xl" data-testid="chat-panel">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-800">
              <Sparkles size={16} color="#D4AF37" />
              <div>
                <p className="font-serif text-lg leading-none">Research Concierge</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#52525B] mt-1">AI assistant · RUO</p>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#D4AF37] text-black" : "bg-[#141414] border border-zinc-800 text-[#E8E8E3]"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="bg-[#141414] border border-zinc-800 px-3.5 py-2.5 text-sm text-[#71717A]" data-testid="chat-typing">Thinking…</div></div>}
            </div>
            <div className="border-t border-zinc-800 p-3 flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about a compound…" data-testid="chat-input"
                className="flex-1 bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37] placeholder:text-[#52525B] transition-colors" />
              <button onClick={send} disabled={busy} data-testid="chat-send"
                className="bg-[#D4AF37] text-black px-4 flex items-center justify-center hover:bg-[#C5A028] transition-colors disabled:opacity-40">
                <Send size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
